import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { HiMapPin } from 'react-icons/hi2';
import { IoAlertCircleOutline, IoCloseOutline } from 'react-icons/io5';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SemFoto } from '../../../../../assets';
import LeadContactModal from '../../../../../components/modals/LeadContactModal';
import { formatarDataEntrega, isColorLight, removerAcentos } from '../../../../../utils/FunctionsGlobals';
import { appendCodsiteQuery } from '../../../../../utils/url';
import styles from './ListPiecesCondominio.module.css';

type NormalizedCard = {
    id: number;
    nome: string;
    link: string;
    localizacao: string;
    endereco: string;
    tipoNome: string;
    categoriaNome: string;
    areaRange: string;
    priceRange: string;
    descricao: string;
    dormitoriosText: string;
    entregaText: string;
    situacao: string;
    preco_min: number;
    fotos: Array<{ id: number; url_foto: string; descricao: string }>;
};

const formatMoney = (value: any) => {
    const price = Number(value);
    if (Number.isNaN(price) || price <= 0) {
        return '';
    }
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0
    }).format(price);
};

const formatMoneyCompact = (value: any) => {
    const price = Number(value);
    if (Number.isNaN(price) || price <= 0) {
        return '';
    }
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
        notation: 'compact',
        compactDisplay: 'short'
    }).format(price);
};

const parseAreaValue = (value: any) => {
    if (value === undefined || value === null) return undefined;
    const normalized = String(value).replace(',', '.').replace(/[^\d.]/g, '');
    const parsed = Number(normalized);
    if (Number.isNaN(parsed) || parsed <= 0) return undefined;
    return parsed;
};

const formatArea = (value: number) => {
    if (!Number.isFinite(value)) return '';
    const hasDecimal = value % 1 !== 0;
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: hasDecimal ? 1 : 0,
        maximumFractionDigits: hasDecimal ? 1 : 0
    }).format(value);
};

const getAreaRangeText = (item: any) => {
    const min = parseAreaValue(item?.area_privativa_min || item?.are_privativa_min);
    const max = parseAreaValue(item?.area_privativa_max);

    if (min && max) {
        if (min === max) return `${formatArea(min)} m²`;
        return `${formatArea(min)}-${formatArea(max)} m²`;
    }
    if (min) return `${formatArea(min)} m²`;
    if (max) return `${formatArea(max)} m²`;
    return '';
};

const stripHtml = (value: any) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const getCondominioLink = (site: any, nome: string, id?: number, codsite?: any, origin?: any) => {
    if (id && Number(id) > 0) {
        return appendCodsiteQuery(`/empreendimento/${id}`, codsite, origin);
    }

    const transacao = site?.transacoes?.[0]?.toLowerCase() || 'venda';
    const slug = removerAcentos(String(nome || '-'))
        .toLowerCase()
        .replaceAll('/', '-')
        .replaceAll('?', '-')
        .replaceAll('.', '-')
        .replaceAll('\n', '-')
        .replaceAll(' ', '-');

    return appendCodsiteQuery(`/imoveis/${transacao}/-/-/${slug || '-'}/-`, codsite, origin);
};

const getStorageArray = (key: string) => {
    if (typeof window === 'undefined') return [];
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((id: any) => Number(id))
            .filter((id: any) => Number.isFinite(id) && id > 0);
    } catch (error) {
        return [];
    }
};

const getDormitoriosText = (item: any) => {
    const dormitorios = Number(item?.dormitorios || 0);
    const suites = Number(item?.suites || 0);

    if (dormitorios > 0) return dormitorios === 1 ? '1 quarto' : `${dormitorios} quartos`;
    if (suites > 0) return suites === 1 ? '1 suite' : `${suites} suites`;
    return '';
};

const getEntregaText = (item: any) => {
    const value = Number(item?.data_entrega || 0);
    if (!value) return '';
    const formatted = formatarDataEntrega(value);
    if (!formatted || formatted === 'false') return '';
    return `Entrega em ${formatted}`;
};

const getMainChipText = (item: any) => {
    const categoria = String(item?.categoriaNome || '').trim();
    const situacao = String(item?.situacao || '').trim();
    const merged = `${categoria} ${situacao}`.toLowerCase();

    if (merged.includes('planta')) return 'Na planta';
    if (merged.includes('lanc')) return 'Lancamento';
    if (merged.includes('pronto')) return 'Pronto novo';
    return categoria || situacao || 'Empreendimento';
};

const ListPiecesCondominio: React.FC<{
    allCompany: any;
    site: any;
    allCities: any;
    allTypesMap?: Record<string, string>;
    onLoadMore?: () => void;
    hasMore?: boolean;
    loadingMore?: boolean;
    loadingInitial?: boolean;
    origin?: any;
    codsite?: any;
}> = ({
    allCompany,
    site,
    allCities,
    allTypesMap,
    onLoadMore,
    hasMore,
    loadingMore,
    loadingInitial,
    origin,
    codsite
}) => {
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const loadLockRef = useRef(false);
    const storagePrefix = useMemo(() => `condominios:${site?.id || 'default'}`, [site?.id]);

    const [ignoredIds, setIgnoredIds] = useState<number[]>([]);
    const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
    const [priceAlertIds, setPriceAlertIds] = useState<number[]>([]);
    const [expandedDescriptions, setExpandedDescriptions] = useState<Record<number, boolean>>({});
    const [leadOpen, setLeadOpen] = useState(false);
    const [selectedLeadCard, setSelectedLeadCard] = useState<NormalizedCard | null>(null);

    useEffect(() => {
        if (!loadingMore) loadLockRef.current = false;
    }, [loadingMore]);

    useEffect(() => {
        if (!hasMore || !onLoadMore || loadingMore) return;

        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (!first?.isIntersecting || loadLockRef.current) return;
                loadLockRef.current = true;
                onLoadMore();
            },
            { rootMargin: '700px 0px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [allCompany?.length, hasMore, loadingMore, onLoadMore]);

    useEffect(() => {
        setIgnoredIds(getStorageArray(`${storagePrefix}:ignored`));
        setFavoriteIds(getStorageArray(`${storagePrefix}:favorites`));
        setPriceAlertIds(getStorageArray(`${storagePrefix}:alerts`));
    }, [storagePrefix]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(`${storagePrefix}:ignored`, JSON.stringify(ignoredIds));
    }, [ignoredIds, storagePrefix]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(`${storagePrefix}:favorites`, JSON.stringify(favoriteIds));
    }, [favoriteIds, storagePrefix]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(`${storagePrefix}:alerts`, JSON.stringify(priceAlertIds));
    }, [priceAlertIds, storagePrefix]);

    const normalizedCards = useMemo<NormalizedCard[]>(() => {
        return (allCompany || [])
            .map((item: any) => {
            let cidadeNome = item?.cidade || '';
            if (!cidadeNome && item?.id_cidade && allCities !== undefined) {
                const cidade = allCities.find((city: any) => city.id == item?.id_cidade);
                cidadeNome = cidade?.nome || '';
            }

            const bairroNome = item?.bairro || '';
            const localizacao = [bairroNome, cidadeNome].filter(Boolean).join(', ');
            const tiposDetalhados = Array.isArray(item?.tipos_emp_detalhes)
                ? item.tipos_emp_detalhes.map((tipo: any) => tipo?.nome).filter(Boolean)
                : [];
            const tipoNome = tiposDetalhados.length > 0
                ? tiposDetalhados.join(', ')
                : (String(item?.tipos_emp || '').trim() || allTypesMap?.[String(item?.id_tipos || '')] || 'Empreendimento');
            const categoriaNome = String(item?.categoria_empreendimento?.nome || '').trim();
            const areaRange = getAreaRangeText(item);
            const priceRange = formatMoney(item?.preco_min);
            const link = getCondominioLink(site, item?.nome, Number(item?.id), codsite, origin);
            const descricao = stripHtml(item?.descricao || '');
            const dormitoriosText = getDormitoriosText(item);
            const entregaText = getEntregaText(item);

            const fotos = Array.isArray(item?.fotos)
                ? item.fotos
                    .map((foto: any) => ({
                        id: Number(foto?.id || 0),
                        url_foto: String(foto?.url_foto || ''),
                        descricao: String(foto?.descricao || item?.nome || '')
                    }))
                    .filter((foto: any) => Boolean(foto?.url_foto))
                : [];

            return {
                ...item,
                id: Number(item?.id || 0),
                nome: String(item?.nome || ''),
                localizacao,
                endereco: String(item?.endereco || ''),
                tipoNome,
                categoriaNome,
                areaRange,
                priceRange,
                link,
                descricao,
                dormitoriosText,
                entregaText,
                situacao: String(item?.situacao || ''),
                preco_min: Number(item?.preco_min || 0),
                fotos
            };
            });
    }, [allCities, allCompany, allTypesMap, codsite, origin, site]);

    const visibleCards = useMemo(
        () => normalizedCards.filter((item: NormalizedCard) => !ignoredIds.includes(Number(item?.id))),
        [ignoredIds, normalizedCards]
    );

    const showEmptyState = !loadingInitial && !loadingMore && !hasMore && visibleCards.length === 0;

    const toggleFavorite = (id: number) => {
        setFavoriteIds((prev) => (
            prev.includes(id) ? prev.filter((current) => current !== id) : [...prev, id]
        ));
    };

    const ignoreCard = (id: number) => {
        setIgnoredIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    };

    const toggleDescription = (id: number) => {
        setExpandedDescriptions((prev) => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const openLeadModal = (item: NormalizedCard) => {
        setSelectedLeadCard(item);
        setLeadOpen(true);
    };

    const cardAccent = site?.cor2 || '#004766';
    const cardAccentInk = isColorLight(cardAccent) ? (site?.cor11 || '#111827') : (site?.cor13 || '#ffffff');
    const cardSurface = site?.cor5 || '#ffffff';
    const cardSurfaceInk = isColorLight(cardSurface) ? (site?.cor11 || '#111827') : (site?.cor13 || '#f8fafc');
    const cardActionInk = isColorLight(cardAccent) === isColorLight(cardSurface) ? cardSurfaceInk : cardAccent;

    return (
        <section className={styles.wrapper}>
            {showEmptyState ? (
                <div className={styles.emptyState}>
                    <h3>Nenhum empreendimento encontrado</h3>
                    <p>Tente ajustar os filtros para ampliar a busca.</p>
                </div>
            ) : null}

            <div className={styles.grid}>
                {visibleCards.map((item: NormalizedCard) => {
                    const itemId = Number(item?.id || 0);
                    const fotos = item?.fotos?.length > 0
                        ? item.fotos
                        : [{ id: 0, url_foto: SemFoto.src, descricao: item?.nome || 'Sem foto' }];
                    const showMore = String(item?.descricao || '').length > 120;
                    const expanded = Boolean(expandedDescriptions[itemId]);
                    const isFavorited = favoriteIds.includes(itemId);
                    const isPriceAlertOn = priceAlertIds.includes(itemId);
                    const mainChip = getMainChipText(item);

                    return (
                        <article
                            className={styles.card}
                            key={itemId}
                            style={{
                                ['--condo-accent' as any]: cardAccent,
                                ['--condo-accent-ink' as any]: cardAccentInk,
                                ['--condo-surface' as any]: cardSurface,
                                ['--condo-surface-ink' as any]: cardSurfaceInk,
                                ['--condo-action-ink' as any]: cardActionInk
                            }}
                        >
                            <div className={styles.mediaArea}>
                                <div className={styles.chipsOverlay}>
                                    <span className={styles.mainChip}>{mainChip}</span>
                                    {item?.entregaText ? (
                                        <span className={styles.dateChip}>{item.entregaText}</span>
                                    ) : null}
                                </div>

                                <Swiper
                                    modules={[Navigation, Pagination]}
                                    pagination={{ clickable: true }}
                                    navigation
                                    className={styles.cardSwiper}
                                >
                                    {fotos.map((foto, index) => (
                                        <SwiperSlide key={`${itemId}-${foto?.id || index}`}>
                                            <Link prefetch={false} href={item.link} className={styles.slideLink}>
                                                <Image
                                                    width={640}
                                                    height={420}
                                                    alt={foto?.descricao || `Foto do empreendimento ${item?.nome || ''}`}
                                                    src={foto?.url_foto || SemFoto.src}
                                                    className={styles.imageCondo}
                                                />
                                            </Link>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>

                            <div className={styles.content}>
                                <Link prefetch={false} href={item.link} className={styles.titleLink}>
                                    <h3 className={styles.title}>
                                        {item?.tipoNome ? `${item.tipoNome} a venda no` : 'Empreendimento'}
                                        {' '}
                                        <strong>{item?.nome}</strong>
                                    </h3>
                                </Link>

                                {item?.descricao ? (
                                    <div className={styles.descriptionBox}>
                                        <p className={`${styles.descriptionText} ${!expanded ? styles.descriptionClamped : ''}`}>
                                            {item.descricao}
                                        </p>
                                        {showMore ? (
                                            <button
                                                type="button"
                                                className={styles.descriptionToggle}
                                                onClick={() => toggleDescription(itemId)}
                                            >
                                                {expanded ? 'menos' : 'mais'}
                                            </button>
                                        ) : null}
                                    </div>
                                ) : null}

                                {item?.localizacao ? (
                                    <p className={styles.location}>
                                        <span className={styles.locationPrimary}>{item.localizacao}</span>
                                        {item?.endereco ? (
                                            <span className={styles.locationStreet}>
                                                <HiMapPin size={14} />
                                                {item.endereco}
                                            </span>
                                        ) : null}
                                    </p>
                                ) : null}

                                <div className={styles.detailsLine}>
                                    {item?.categoriaNome ? (
                                        <span>{item.categoriaNome}</span>
                                    ) : null}
                                    {item?.dormitoriosText ? (
                                        <span>{item.dormitoriosText}</span>
                                    ) : null}
                                    {item?.areaRange ? (
                                        <span>{item.areaRange}</span>
                                    ) : null}
                                </div>

                                {item?.priceRange ? (
                                    <p className={styles.priceText}>
                                        A partir de <strong>{formatMoneyCompact(item.preco_min) || item.priceRange}</strong>
                                    </p>
                                ) : null}

                                <div className={styles.alertRow}>
                                    <div className={styles.alertLabel}>
                                        <IoAlertCircleOutline size={18} />
                                        <span>Avise-me se o preço mudar</span>
                                    </div>
                                    <label className={styles.switch}>
                                        <input
                                            type="checkbox"
                                            checked={isPriceAlertOn}
                                            onChange={() => openLeadModal(item)}
                                        />
                                        <span className={styles.slider} />
                                    </label>
                                </div>

                                <div className={styles.ctaRow}>
                                    <Link prefetch={false} href={item.link} className={styles.primaryButton}>
                                        Ver apartamentos
                                    </Link>
                                </div>

                                <div className={styles.secondaryActions}>
                                    <button
                                        type="button"
                                        className={styles.secondaryButton}
                                        onClick={() => ignoreCard(itemId)}
                                    >
                                        <IoCloseOutline size={18} />
                                        ignorar
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.secondaryButton} ${isFavorited ? styles.secondaryButtonActive : ''}`}
                                        onClick={() => toggleFavorite(itemId)}
                                    >
                                        {isFavorited ? <FaHeart size={15} /> : <FaRegHeart size={15} />}
                                        favoritar
                                    </button>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>

            {loadingInitial ? (
                <div className={styles.loadingMessage}>Carregando empreendimentos...</div>
            ) : null}

            {loadingMore ? (
                <div className={styles.loadingMessage}>Buscando mais resultados...</div>
            ) : null}

            {hasMore ? (
                <div ref={sentinelRef} className={styles.scrollSentinel} />
            ) : null}

            {!hasMore && visibleCards.length > 0 ? (
                <div className={styles.endMessage}>Voce chegou ao fim da lista.</div>
            ) : null}

            <LeadContactModal
                open={leadOpen}
                onClose={() => setLeadOpen(false)}
                site={site}
                origin={origin}
                codsite={codsite}
                title={selectedLeadCard?.nome ? `Avise-me quando o preço do empreendimento ${selectedLeadCard.nome} mudar` : 'Avise-me quando o preço mudar'}
                message={selectedLeadCard?.nome ? `Tenho interesse no empreendimento ${selectedLeadCard.nome}` : 'Tenho interesse neste empreendimento'}
                source="condominios-anotacao"
                entityId={0}
            />
        </section>
    );
};

export default ListPiecesCondominio;

export const getServerSideProps = async () => ({ notFound: true });
